<template>
    <v-card tile flat>
        <v-dialog v-if="targetAssetHashHistory != -1" v-model="showHashChangesDialog" scrollable max-width="800px">
            <v-card>
                <v-card-title class="headline grey lighten-2 text-uppercase">{{ hashLabel }} changes</v-card-title>
                <v-card-text>
                    <v-list-item>
                        <v-list-item-content>
                            <div class="text-h6">
                                Updated by {{ assetHashHistory[targetAssetHashHistory].msp_id }}
                                <span class="text-subtitle-1 text--secondary">
                                    @ {{ assetHashHistory[targetAssetHashHistory].remote_geolocation }}
                                    <span>via <a>{{ assetHashHistory[targetAssetHashHistory].app_name }}</a></span>
                                </span>
                            </div>
                            <span class="caption text--secondary">{{ assetHashHistory[targetAssetHashHistory].url }}</span>
                            <div class="caption text--secondary">{{ toLongFormatWithoutMin(assetHashHistory[targetAssetHashHistory].created_at) }}</div>         
                        </v-list-item-content>
                    </v-list-item>
                    <vue-json-compare v-if="targetAssetHashHistory != (assetHashHistory.length - 1)" :oldData="JSON.parse(assetHashHistory[targetAssetHashHistory+1].value_content)" :newData="JSON.parse(assetHashHistory[targetAssetHashHistory].value_content)"></vue-json-compare>
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" text @click="showHashChangesDialog = false">Done</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-card-title v-if="size == 'regular'" class="text-h4 font-weight-bold">{{ title }}</v-card-title>
        <v-card-title v-if="size == 'small'" class="text-h5 font-weight-bold">{{ title }}</v-card-title>

        <div class="main-block-wrapper">
            <template v-for="(record, index) in sortedMilestones">
                <div v-bind:key="index" :class="(sortedMilestones.length < 2) ? 'h-block-placeholder' : 'h-block' ">
                    <div v-if="sortedMilestones[index + 1]"
                        class="duration"
                        :style="{ backgroundColor: getHeatColorAndText(legTimes, legTime(record.start_time, sortedMilestones[index+1].start_time)).color }">
                        <div class="ml-3 text-h4 font-weight-bold"
                            :class="getHeatColorAndText(legTimes, legTime(record.start_time, sortedMilestones[index+1].start_time)).text">
                            {{ getFriendlyDurationSplitUnit(record.start_time, sortedMilestones[index+1].start_time, ['d'])[0] }} 
                            <span class="text-h6 font-weight-light">&nbsp;{{ getFriendlyDurationSplitUnit(record.start_time, sortedMilestones[index+1].start_time, ['d'])[1] }}</span> 
                        </div>
                    </div>
                    <div v-else-if="sortedMilestones.length < 2" class="duration-placeholder">
                            <div class="mx-3 text-subtitle-1 text--secondary text-uppercase">
                                Add one more milestone to see the timeline
                            </div>
                    </div>
                    <div v-else class="duration"></div>
                    <div class="pointer"></div>
                    <div class="ml-n3">
                        <v-avatar size="32" v-if="hasOperation(record.operations, KEY_TRACE_OPERATION_TYPE_READ)">
                            <v-tooltip bottom>
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon size="18" class="light-blue" dark  v-bind="attrs" v-on="on">mdi-eye-circle</v-icon>
                                </template>
                                <span>Read</span>
                            </v-tooltip>
                        </v-avatar>
                        <v-avatar size="32" v-if="hasOperation(record.operations, KEY_TRACE_OPERATION_TYPE_DELETE)">
                            <v-tooltip bottom>
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon size="18" class="blue-grey" dark v-bind="attrs" v-on="on">mdi-delete</v-icon>
                                </template>
                                <span>Delete</span>
                            </v-tooltip>
                        </v-avatar>
                        <v-avatar size="32" v-if="record.web_txn_id == creationInfo[2]">
                            <v-tooltip bottom>
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon size="18" class="teal" dark v-bind="attrs" v-on="on">mdi-folder-plus</v-icon>
                                </template>
                                <span>Create</span>
                            </v-tooltip>
                        </v-avatar>
                        <v-avatar size="32" v-else-if="hasOperation(record.operations, KEY_TRACE_OPERATION_TYPE_WRITE)">
                            <v-tooltip bottom>
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon size="18" class="brown" dark v-bind="attrs" v-on="on">mdi-pencil</v-icon>
                                </template>
                                <span>Update</span>
                            </v-tooltip>
                        </v-avatar>                                                  
                        <v-avatar size="32" v-if="(index > 0) && record.tracked_hash_values && record.tracked_hash_values.split(',').length > 1">
                            <v-tooltip bottom>
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon size="18" class="indigo" dark v-bind="attrs" v-on="on">mdi-pound</v-icon>
                                </template>
                                <span>{{ hashLabel }} updated</span>
                            </v-tooltip>
                        </v-avatar>
                    </div>
                    <div class="my-2"  v-if="(index > 0) && record.tracked_hash_values && record.tracked_hash_values.split(',').length > 1">
                        <v-chip class="" @click="displayHashChanges(record, index)" small>{{ hashLabel }} changes</v-chip>
                    </div>
                    <div v-else class="my-5"></div>
                    <div class="detail">               
                        <div class="caption">{{ toLongFormatWithoutMin(record.start_time) }}</div>
                        <div class="text-body-1 font-weight-medium">{{ record.msp_id }}</div>
                        <div>via <a>{{ record.app_name }}</a></div>
                        <div class="text--secondary">{{ record.uri }}</div>
                    </div>
                </div>
            </template>
            <div class="h-block-placeholder">
                <div v-if="sortedMilestones.length == 0" class="duration-placeholder">
                    <div class="mx-3 text-subtitle-1 text--secondary text-uppercase">
                        Click <v-icon size="28" disabled color="#5E35B1">mdi-plus-circle</v-icon> on the left to add a milestone
                    </div>
                </div>
            </div>
        </div>
        <template v-if="sortedMilestones.length > 1">
            <v-card-title v-if="size == 'regular'" class="text-h4 font-weight-bold mt-4">Relative Performance Comparison</v-card-title>
            <v-card-title v-if="size == 'small'" class="text-h5 font-weight-bold mt-4">Relative Performance Comparison</v-card-title>
        </template>

        <v-card-text>
            <BarWithContrast
                v-if="sortedMilestones.length > 1"
                :mainValue="bizTxnTTLTime"
                :contrastValue="getFriendlyDurationTime(contrastTime, ['d']).split(' ')[0]"
                v-bind:contrastPeriodCode="getContrastTimeSelection"
                unit="days"
                color="indigo"
            />        
            <div v-else>

            </div>
        </v-card-text>
    </v-card>
</template>

<script>
    import { mapGetters, mapActions } from 'vuex'
    import ColorHelper from '@/helpers/color_helper'
    import TimeHelper from '@/helpers/time_helper'
    import BarWithContrast from '@/components/BarWithContrast.vue'
    import Constants from '@/config/constants'
    import VueJsonCompare from 'vue-json-compare'

    export default {

        components: {
            BarWithContrast, VueJsonCompare
        },

        name: 'BusinessTransaction',

        props: {
            title: String,
            milestones: Array,
            assetHashHistory: Array,
            creationInfo: Array,
            contrastTime: Number,
            size: { type: String, default: 'regular' },
        },
        mounted: function() {
        },
        created () {
            this.KEY_TRACE_OPERATION_TYPE_READ = Constants.KEY_TRACE_OPERATION_TYPE_READ;
            this.KEY_TRACE_OPERATION_TYPE_WRITE = Constants.KEY_TRACE_OPERATION_TYPE_WRITE;
            this.KEY_TRACE_OPERATION_TYPE_DELETE = Constants.KEY_TRACE_OPERATION_TYPE_DELETE;
        },
        watch: {
            milestones: function (newVal, oldVal) {
            }
        },    
        computed: {
            ...mapGetters([
                'getContrastTimeSelection'
            ]),
            sortedMilestones: function() {
                return [...this.milestones].sort(function(a,b){
                    // Turn your strings into dates, and then subtract them
                    // to get a value that is either negative, positive, or zero.
                    return new Date(a.start_time) - new Date(b.start_time);
                });         
            },
            legTimes: function() {
                let times = this.sortedMilestones.map( (ele, index, arr) => {
                    if (index == arr.length -1) {
                        return null
                    } else {
                        return new Date(arr[index+1].start_time) - new Date(ele.start_time)
                    }
                })
                console.log("Leg times", times.nonNull())
                return times.nonNull();
            },
            bizTxnTTLTime: function() {
                return this.getFriendlyDurationSplitUnit(this.sortedMilestones[0].start_time, this.sortedMilestones[this.sortedMilestones.length -1].start_time, ['d'])[0] // first element is value
            },
            bizTxnTTLTimeUnit: function() {
                return this.getFriendlyDurationSplitUnit(this.sortedMilestones[0].start_time, this.sortedMilestones[this.sortedMilestones.length -1].start_time, ['d'])[1] // first element is value
            },
            bizTxnTTLTimeUnitOneChar: function() {
                return [ this.bizTxnTTLTimeUnit[0] ] // First char in the unit
            },
            hashLabel: function() {
                return (this.milestones.length > 0) ? this.milestones[0].tracked_hash_label : 'Hash'         
            }
        },

        methods: {
            ...ColorHelper,            
            ...TimeHelper,
            legTime: function(startTimeA, startTimeB) {
                return Math.abs( new Date(startTimeA) - new Date(startTimeB) );
            },
            hasOperation: function(operations, operationType) {
                return operations.split(',').includes(operationType.toString());
            },
            displayHashChanges(record, index) {
                this.showHashChangesDialog = true
                for (let i = 0; i < this.assetHashHistory.length ; i++) {
                    if (record.web_txn_id == this.assetHashHistory[i].web_txn_id) {
                        this.targetAssetHashHistory = i
                        break;
                    }
                }
            }
        },

        data: () => ({
            showHashChangesDialog: false,
            targetAssetHashHistory: -1,
            milestoneSamples: []
        }),
    }
</script>

<style scoped lang="scss">

    .contrast-block-wrapper {
        display: flex;
        width: 100%;
    }

    .contrast {
        flex-basis: 90%;
        background-color: #BBDEFB;
        height: 20px;
        margin: 10px 0px;
        border-bottom-left-radius: 4px;
        border-top-left-radius: 4px;  
        border-bottom-right-radius: 4px;
        border-top-right-radius: 4px;    
    }

    .contrast-label {
        flex-basis: 10%;
        display: flex;
        flex-direction: column;
    }

    .contrast-label > span {
        line-height: 1.25rem;
    }

    .main-block-wrapper {
        display: flex;
        width: 100%;
        overflow-x: auto;
        padding-left: 12px;
    }

    .h-block-placeholder {
        display: flex;
        flex-direction: column;
        min-width: 200px;
        min-height: 240px;
    }

    .h-block {
        display: flex;
        flex-direction: column;
        max-width: 200px;
        min-width: 200px;
        min-height: 240px;
    }

    .duration {
        display: flex;
        align-items: center;
        height: 60px;
        border-left: 1px solid white;
        border-bottom-left-radius: 4px;
        border-top-left-radius: 4px;  
        border-bottom-right-radius: 4px;
        border-top-right-radius: 4px;    
        -webkit-transition: all 1s ease;
        -moz-transition: all 1s ease;
        -o-transition: all 1s ease;
        transition: all 1s ease;
    }

    .duration-placeholder {
        display: flex;
        align-items: center;
        height: 60px;
        border: 2px dashed grey;
        border-bottom-left-radius: 4px;
        border-top-left-radius: 4px;  
        border-bottom-right-radius: 4px;
        border-top-right-radius: 4px;    
        -webkit-transition: all 1s ease;
        -moz-transition: all 1s ease;
        -o-transition: all 1s ease;
        transition: all 1s ease;
    }

    .pointer {
        height: 32px;
        border-left: 2px solid #1976D2;
        -webkit-transition: all 1s ease;
        -moz-transition: all 1s ease;
        -o-transition: all 1s ease;
        transition: all 1s ease;
    }

    .pointer-icon {
        color: #1976D2;
        justify-content: left;
        margin-top: -3px;
        margin-left: -15px;
    }

    .detail {
        display: flex;
        flex-direction: column;
        padding-right: 8px;
        -webkit-transition: all 1s ease;
        -moz-transition: all 1s ease;
        -o-transition: all 1s ease;
        transition: all 1s ease;
    }

</style>